import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { App, BuildTrigger, Environment, Project } from '@hangar/shared';
import { toast } from 'sonner';
import { useProfiles } from '@/api/hooks';
import { useBuildTriggerMutations, useBuildTriggers } from '@/api/hooksTriggers';
import type { Profile } from '@/api/schemasProfile';
import { stagger } from '@/lib/motion';
import { useCanConfigure } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { ConfirmArchiveDialog } from '@/components/ConfirmArchiveDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { RowActions } from '@/components/RowActions';
import { Dot, Tag } from '@/components/badges';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { BuildTriggerDialog, type TriggerDialogState } from './BuildTriggerDialog';
import { lastActivity, newTriggerValues, pathsText, targetText, valuesOf, webhookState, type Tone } from './buildTriggersModel';

const DOT: Record<Tone, 'succeeded' | 'queued' | 'failed' | 'subtle'> = { success: 'succeeded', warning: 'queued', danger: 'failed', neutral: 'subtle' };

/**
 * "Build triggers" (plan P33, SPEC decision 31): builds that start when the app's repository receives a push. The list, the
 * webhook Hangar keeps on the repository (with Retry when the token lacks the permission), the last push and the build it
 * started; add/edit in a dialog. Without triggers, builds start from Run build only.
 */
export function BuildTriggersCard({ app, project }: { app: App; project: Project }) {
  const triggers = useBuildTriggers(app.id);
  const profiles = useProfiles(app.id);
  const can = useCanConfigure(app.projectId);
  const [dialog, setDialog] = useState<TriggerDialogState | null>(null);
  const environments = project.environments ?? [];
  const live = profiles.data ?? [];
  const add = () => setDialog({ mode: 'create', values: newTriggerValues({ workingDirectory: app.workingDirectory, defaultBranch: app.source?.defaultBranch ?? null }, live, environments) });
  const blocked = !app.source ? 'Link the repository first' : live.length === 0 ? 'Add a build profile first' : undefined;
  return (
    <Card className="enter min-w-0" style={stagger(3)} data-testid="app-triggers-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="bolt" size={18} className="text-subtle" />
          Build triggers {triggers.data ? <Tag className="tabular">{triggers.data.data.length}</Tag> : null}
        </CardTitle>
        <CardDescription>Builds that start by themselves when the repository receives a push</CardDescription>
        {can.hidden ? null : (
          <CardAction>
            <GatedButton variant="outline" data-testid="app-triggers-add" can={can} blockedReason={blocked} onClick={add}>
              <Icon name="add" size={18} />
              Add trigger
            </GatedButton>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <TriggersBody app={app} project={project} query={triggers} profiles={live} environments={environments} onEdit={(t) => setDialog({ mode: 'edit', trigger: t, values: valuesOf(t) })} />
      </CardContent>
      <BuildTriggerDialog appId={app.id} state={dialog} profiles={live} environments={environments} onOpenChange={(open) => { if (!open) setDialog(null); }} />
    </Card>
  );
}

type Query = ReturnType<typeof useBuildTriggers>;

function TriggersBody({ app, project, query, profiles, environments, onEdit }: { app: App; project: Project; query: Query; profiles: Profile[]; environments: Environment[]; onEdit: (t: BuildTrigger) => void }) {
  if (query.isLoading && !query.data) return <div className="flex flex-col gap-2" aria-busy="true">{[0, 1].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  if (query.isError && !query.data) return <ErrorState compact error={query.error} onRetry={() => void query.refetch()} />;
  const list = query.data!;
  return (
    <>
      <WebhookBanner app={app} list={list} />
      {list.data.length === 0 ? (
        <EmptyState compact testId="app-triggers-empty" icon="bolt" title="No build triggers yet"
          description="Without triggers, builds start from Run build. Add one and a push to its branch builds the app, only when the app's folder changed." />
      ) : (
        <ul className="flex flex-col divide-y rounded-lg border" data-testid="app-triggers-list">
          {list.data.map((t) => <TriggerRow key={t.id} app={app} project={project} trigger={t} profiles={profiles} environments={environments} onEdit={() => onEdit(t)} />)}
        </ul>
      )}
    </>
  );
}

function WebhookBanner({ app, list }: { app: App; list: NonNullable<Query['data']> }) {
  const state = webhookState(list.webhook);
  const can = useCanConfigure(app.projectId);
  const { retry } = useBuildTriggerMutations(app.id);
  if (list.webhook.status === 'not_needed' && list.data.length === 0) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="app-triggers-webhook" data-status={list.webhook.status}>
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <span className="inline-flex items-center gap-2 text-[13px] font-medium text-foreground"><Dot tone={DOT[state.tone]} />{state.label}</span>
        {state.help ? <span className="min-w-0 text-[13px] text-muted-foreground" data-testid="app-triggers-webhook-help">{state.help}</span> : null}
        {state.retry && !can.hidden ? (
          <GatedButton size="sm" variant="outline" data-testid="app-triggers-retry" can={can} loading={retry.isPending} onClick={() => retry.mutate(undefined, { onSuccess: (r) => (r.webhook.status === 'registered' ? toast.success('Webhook registered') : toast.message(webhookState(r.webhook).label)) })}>
            <Icon name="refresh" size={16} />
            Retry
          </GatedButton>
        ) : null}
      </div>
      <ProblemAlert error={retry.error} testId="app-triggers-retry-problem" />
    </div>
  );
}

function TriggerRow({ app, project, trigger: t, profiles, environments, onEdit }: { app: App; project: Project; trigger: BuildTrigger; profiles: Profile[]; environments: Environment[]; onEdit: () => void }) {
  const can = useCanConfigure(app.projectId);
  const { update, remove } = useBuildTriggerMutations(app.id);
  const [confirm, setConfirm] = useState(false);
  const activity = lastActivity(t);
  const subject = `trigger ${t.branchPattern}`;
  return (
    <li className="flex min-w-0 items-start gap-3 px-3.5 py-3 sm:px-4" data-testid={`app-trigger-${t.branchPattern}`} data-enabled={t.enabled}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[14px]">
          <span className="text-muted-foreground">Push to</span>
          <span className="truncate font-mono text-[13px] font-medium text-foreground">{t.branchPattern}</span>
          <Icon name="arrow_forward" size={14} className="text-subtle" />
          <span className="min-w-0 truncate text-foreground" data-testid="app-trigger-target">{targetText(t, profiles, environments)}</span>
        </div>
        <span className="truncate font-mono text-[12px] text-subtle" title={pathsText(t.paths)}>{pathsText(t.paths)}</span>
        {activity ? <Activity projectSlug={project.slug} activity={activity} /> : <span className="text-[13px] text-subtle">No push yet</span>}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Switch data-testid="app-trigger-enabled" aria-label={`${t.enabled ? 'Turn off' : 'Turn on'} ${subject}`} checked={t.enabled} disabled={!can.allowed || update.isPending} className="m-2.5"
          onCheckedChange={(enabled) => update.mutate({ id: t.id, body: { enabled } }, { onError: (e) => toast.error(e.message) })} />
        {can.hidden ? null : (
          <RowActions variant="menu" subject={subject} testId="app-trigger-actions" actions={[
            { key: 'edit', label: 'Edit', icon: 'edit', onSelect: onEdit, ...(can.allowed ? {} : { disabledReason: can.reason ?? 'Requires admin or maintainer' }), testId: 'app-trigger-edit' },
            { key: 'delete', label: 'Delete', icon: 'delete', destructive: true, onSelect: () => setConfirm(true), ...(can.allowed ? {} : { disabledReason: can.reason ?? 'Requires admin or maintainer' }), testId: 'app-trigger-delete' },
          ]} />
        )}
      </div>
      <ConfirmArchiveDialog open={confirm} onOpenChange={setConfirm} testId="app-trigger-delete-dialog" title="Delete this build trigger?" confirmLabel="Delete" busy={remove.isPending}
        description={`Pushes to ${t.branchPattern} stop starting builds. Builds it already started are kept.`}
        impact={[{ text: 'When no other trigger needs it, Hangar removes its webhook from the repository' }]}
        onConfirm={() => remove.mutate(t.id, { onSuccess: () => { setConfirm(false); toast.success('Build trigger deleted'); } })} />
    </li>
  );
}

function Activity({ projectSlug, activity }: { projectSlug: string; activity: NonNullable<ReturnType<typeof lastActivity>> }) {
  const tone = activity.tone === 'danger' ? 'text-destructive' : activity.tone === 'success' ? 'text-foreground' : 'text-muted-foreground';
  return (
    <span className={cn('flex min-w-0 flex-wrap items-center gap-x-2 text-[13px]', tone)} data-testid="app-trigger-activity">
      <span className="min-w-0 break-words">{activity.text}</span>
      {activity.buildId ? (
        <Link to="/projects/$slug/builds/$buildId" params={{ slug: projectSlug, buildId: activity.buildId }} data-testid="app-trigger-build-link" className="inline-flex min-h-11 items-center gap-1 rounded-sm font-medium text-primary hover:underline sm:min-h-0">
          Open build #{activity.buildNumber}
          <Icon name="arrow_forward" size={14} />
        </Link>
      ) : null}
    </span>
  );
}
